/**
 * GateTimingEditor — edit an interactive-edu slide's narrator gates (pauseAt /
 * resumeAt) from the video-workspace, with a play-along, gate-simulating timeline.
 *
 * Gates live in the same unified source artifact generation writes
 * (source/audio.json → narrator.gates[]). This editor reads them + the signed
 * audio src + wordTimings from the served slide.json and saves timing-only
 * changes through the existing saveSlideEdits pipeline (editData.audio.gates →
 * audio.json → recompile → versioned S3 + DB). The player reloads via SSE.
 *
 * Timeline (mirrors the Kubicle storyboard SeamEditor, gate-flavoured):
 *  - decoded WAVEFORM so silence regions are visible at a glance,
 *  - the real inter-word silences highlighted as snap targets,
 *  - GATE-AWARE PLAYBACK: play holds at each pauseAt exactly like the live
 *    GateRunner; a single "Simulate interaction → Resume" button continues from
 *    resumeAt — so you experience the pause instead of imagining it,
 *  - draggable pause/resume handles that snap to silences.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader2, Save, AlignVerticalJustifyCenter, Play, Pause, Hand } from 'lucide-react';
import { cn } from '@/lib/utils';
import { slidesAPI } from '@/services/api';
import { useNarrationAudioSrc } from '@/hooks/useNarrationAudioSrc';
import { useVideoWorkspaceStore } from '@/stores/videoWorkspaceStore';

interface WordTiming { word: string; startTime: number; endTime: number; confidence?: number; }
interface Gate { id: string; pauseAt: number; resumeAt: number; interaction?: { mechanic?: string }; }
interface Narrator { src?: string; duration?: number; gates?: Gate[]; wordTimings?: WordTiming[]; }

interface GateTimingEditorProps {
  conversationId: string;
  /** The serve/save slide id — "slide-001" in video mode. */
  slideId: string;
  /** Database slide UUID — used to read the take's presigned audio URL. */
  dbSlideId?: string;
  /** The APPLIED SlideAudioVersion number. Without it the editor plays the
   *  DB-current version, which can be a newer un-applied take — the editor
   *  would then play different audio than the gates/wordTimings were authored
   *  against, and every alignment made by ear would be wrong in the player. */
  appliedAudioVersion?: number;
  slidePath?: string;
  className?: string;
}

const SILENCE_MIN = 0.12; // s — gaps wider than this are real breaths
const SNAP_WINDOW = 0.6; // s — drag within this of a silence midpoint snaps to it
const TRACK_H = 80;
const GATE_COLORS = ['#d97706', '#0284c7', '#7c3aed', '#059669', '#db2777'];

function findNarrator(node: any): Narrator | null {
  if (!node || typeof node !== 'object') return null;
  if (Array.isArray(node.gates) || Array.isArray(node.wordTimings)) return node;
  for (const v of Array.isArray(node) ? node : Object.values(node)) {
    const r = findNarrator(v);
    if (r) return r;
  }
  return null;
}

function lastWordEnd(wt?: WordTiming[]): number { return wt && wt.length ? wt[wt.length - 1].endTime : 0; }

function effectiveDuration(n: Narrator): number {
  if (typeof n.duration === 'number' && n.duration > 0 && n.duration < 120) return n.duration;
  return lastWordEnd(n.wordTimings);
}

function computeSilences(wt: WordTiming[]): Array<{ start: number; end: number }> {
  const out: Array<{ start: number; end: number }> = [];
  for (let i = 0; i < wt.length - 1; i++) {
    const gap = wt[i + 1].startTime - wt[i].endTime;
    if (gap >= SILENCE_MIN) out.push({ start: wt[i].endTime, end: wt[i + 1].startTime });
  }
  return out;
}

function snapEdges(silences: Array<{ start: number; end: number }>, t: number) {
  let best: { d: number; start: number; end: number } | null = null;
  for (const s of silences) {
    const mid = (s.start + s.end) / 2;
    const d = Math.abs(mid - t);
    if (!best || d < best.d) best = { d, start: s.start, end: s.end };
  }
  return best && best.d <= SNAP_WINDOW ? best : null;
}

function validate(gates: Gate[], duration: number): string | null {
  const sorted = [...gates].sort((a, b) => a.pauseAt - b.pauseAt);
  for (const g of gates) {
    if (!Number.isFinite(g.pauseAt) || !Number.isFinite(g.resumeAt)) return `Gate "${g.id}": needs numbers`;
    if (g.pauseAt < 0) return `Gate "${g.id}": pause must be ≥ 0`;
    if (g.resumeAt < g.pauseAt) return `Gate "${g.id}": resume must be ≥ pause`;
    if (duration > 0 && g.resumeAt > duration + 0.01) return `Gate "${g.id}": resume exceeds audio (${duration.toFixed(1)}s)`;
  }
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].pauseAt < sorted[i - 1].resumeAt - 0.01) return `Gates "${sorted[i - 1].id}" and "${sorted[i].id}" overlap`;
  }
  return null;
}

const fmt = (t: number) => `${t.toFixed(2)}s`;

export const GateTimingEditor: React.FC<GateTimingEditorProps> = ({ conversationId, slideId, dbSlideId, appliedAudioVersion, slidePath, className }) => {
  const [gates, setGates] = useState<Gate[]>([]);
  const [wordTimings, setWordTimings] = useState<WordTiming[]>([]);
  const [narratorSrc, setNarratorSrc] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  // playback + gate simulation
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [heldGate, setHeldGate] = useState<Gate | null>(null);
  const passedRef = useRef<Set<string>>(new Set());
  const rafRef = useRef<number | null>(null);

  // waveform (synthetic envelope from word timings — no cross-origin audio fetch)
  const [trackW, setTrackW] = useState(320);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ id: string; edge: 'pauseAt' | 'resumeAt' } | null>(null);

  const load = useCallback(async () => {
    if (!conversationId || !slideId) return;
    setLoading(true);
    setStatus(null);
    try {
      // Pin with the manifest-published serve pin (read at fetch time, not
      // subscribed) so repeat panel opens hit the immutable browser cache;
      // a stale pin degrades to the server's no-cache fresh reply.
      const servePin = useVideoWorkspaceStore
        .getState()
        .getServePinForSlide(conversationId, slideId);
      const slide = await slidesAPI.getGsapSlide(slideId, conversationId, servePin);
      const n = findNarrator(slide);
      setGates((n?.gates || []).map((x) => ({ id: x.id, pauseAt: x.pauseAt, resumeAt: x.resumeAt ?? x.pauseAt, interaction: x.interaction })));
      setWordTimings(n?.wordTimings || []);
      setNarratorSrc(n?.src || null);
      setDuration(n ? effectiveDuration(n) : 0);
      passedRef.current.clear();
      setHeldGate(null);
      setDirty(false);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Failed to load gates');
    } finally {
      setLoading(false);
    }
  }, [conversationId, slideId]);

  useEffect(() => { load(); }, [load]);

  // The applied take's presigned URL; narrator.src from the served slide is the fallback.
  const narration = useNarrationAudioSrc(gates.length ? dbSlideId : null, appliedAudioVersion);
  const audioSrc = narration.src || narratorSrc;

  const playAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    const p = a.play();
    if (p && typeof p.then === 'function') {
      p.then(() => setIsPlaying(true)).catch(() => { setIsPlaying(false); setStatus('Audio could not play — try again'); });
    } else {
      setIsPlaying(true);
    }
  };

  // measure track width for the canvas
  useEffect(() => {
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => { for (const e of entries) setTrackW(Math.max(120, e.contentRect.width)); });
    ro.observe(el);
    setTrackW(Math.max(120, el.clientWidth));
    return () => ro.disconnect();
  }, [loading]);

  const scaleDur = duration > 0 ? duration : lastWordEnd(wordTimings) || 1;
  const silences = useMemo(() => computeSilences(wordTimings), [wordTimings]);
  const error = useMemo(() => validate(gates, duration), [gates, duration]);
  const pct = (t: number) => Math.max(0, Math.min(100, (t / scaleDur) * 100));

  // Waveform envelope from the STT word timings (loud inside words, flat in the
  // silences). Built from data we already have — no cross-origin audio fetch, so
  // it works regardless of S3 CORS; the <audio> element still streams playback.
  const peaks = useMemo(() => {
    const N = 600;
    if (!wordTimings.length || scaleDur <= 0) return [] as number[];
    const out = new Array<number>(N);
    let wi = 0;
    for (let i = 0; i < N; i++) {
      const t = (i / N) * scaleDur;
      while (wi < wordTimings.length && wordTimings[wi].endTime < t) wi++;
      const w = wordTimings[wi];
      out[i] = w && t >= w.startTime && t <= w.endTime
        ? Math.min(1, 0.45 + 0.5 * Math.abs(Math.sin(t * 9.1 + wi * 1.7)))
        : 0.05;
    }
    return out;
  }, [wordTimings, scaleDur]);

  // draw waveform
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || peaks.length === 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = trackW * dpr; canvas.height = TRACK_H * dpr;
    canvas.style.width = `${trackW}px`; canvas.style.height = `${TRACK_H}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, trackW, TRACK_H);
    const mid = TRACK_H / 2;
    const barW = trackW / peaks.length;
    ctx.fillStyle = 'rgba(148,163,184,0.55)';
    for (let i = 0; i < peaks.length; i++) {
      const h = Math.max(1, peaks[i] * (TRACK_H * 0.86));
      ctx.fillRect(i * barW, mid - h / 2, Math.max(0.6, barW * 0.7), h);
    }
  }, [peaks, trackW]);

  // gate-aware playback loop
  useEffect(() => {
    const tick = () => {
      const a = audioRef.current;
      if (a) {
        const t = a.currentTime;
        const hit = gates.filter((g) => !passedRef.current.has(g.id) && g.pauseAt <= t + 0.02).sort((x, y) => x.pauseAt - y.pauseAt)[0];
        if (hit) {
          a.pause();
          a.currentTime = hit.pauseAt;
          setCurrentTime(hit.pauseAt);
          setHeldGate(hit);
          setIsPlaying(false);
          return;
        }
        setCurrentTime(t);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    if (isPlaying) rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isPlaying, gates]);

  const xToTime = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return ((clientX - r.left) / r.width) * scaleDur;
  };

  const seek = (t: number) => {
    const a = audioRef.current;
    if (!a) return;
    setHeldGate(null);
    gates.forEach((g) => { if (g.pauseAt >= t - 0.02) passedRef.current.delete(g.id); }); // gates ahead can fire again
    a.currentTime = Math.max(0, Math.min(scaleDur, t));
    setCurrentTime(a.currentTime);
  };

  const simulate = () => {
    const a = audioRef.current;
    if (!a || !heldGate) return;
    passedRef.current.add(heldGate.id);
    a.currentTime = Math.min(scaleDur, heldGate.resumeAt);
    setCurrentTime(a.currentTime);
    setHeldGate(null);
    playAudio();
  };

  const togglePlay = () => {
    if (heldGate) { simulate(); return; }
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { playAudio(); }
    else { a.pause(); setIsPlaying(false); }
  };

  const previewGate = (g: Gate) => {
    const a = audioRef.current;
    if (!a) return;
    setHeldGate(null);
    gates.forEach((x) => { if (x.pauseAt >= g.pauseAt - 1.6) passedRef.current.delete(x.id); });
    a.currentTime = Math.max(0, g.pauseAt - 1.5);
    setCurrentTime(a.currentTime);
    playAudio(); // will hold at g via the playback loop
  };

  const setTiming = (id: string, key: 'pauseAt' | 'resumeAt', value: number) => {
    setGates((prev) => prev.map((g) => (g.id === id ? { ...g, [key]: value } : g)));
    setDirty(true);
    setStatus(null);
  };

  const snap = (id: string) => {
    const g = gates.find((x) => x.id === id);
    if (!g) return;
    const e = snapEdges(silences, g.pauseAt);
    if (!e) return;
    setGates((prev) => prev.map((x) => (x.id === id ? { ...x, pauseAt: Number(e.start.toFixed(3)), resumeAt: Number(e.end.toFixed(3)) } : x)));
    setDirty(true);
    setStatus(null);
  };

  const nudge = (id: string, edge: 'pauseAt' | 'resumeAt', d: number) => {
    const g = gates.find((x) => x.id === id);
    if (!g) return;
    setTiming(id, edge, Number(Math.max(0, g[edge] + d).toFixed(3)));
  };

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      let t = xToTime(e.clientX);
      const snapped = snapEdges(silences, t);
      if (snapped) t = d.edge === 'pauseAt' ? snapped.start : snapped.end;
      setTiming(d.id, d.edge, Number(t.toFixed(3)));
    };
    const up = () => { dragRef.current = null; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silences, scaleDur]);

  const save = async () => {
    if (error || !dirty) return;
    setSaving(true);
    setStatus(null);
    try {
      await slidesAPI.saveSlideEdits({
        conversationId,
        slideId,
        slidePath: slidePath || `slides/${slideId}/slide.json`,
        editData: { summary: { totalChanges: gates.length }, audio: { gates: gates.map((g) => ({ id: g.id, pauseAt: g.pauseAt, resumeAt: g.resumeAt })) } },
        timestamp: new Date().toISOString(),
      });
      setDirty(false);
      setStatus('Saved — player will refresh');
    } catch (e: any) {
      setStatus(e?.response?.data?.details || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className={cn('flex items-center gap-2 p-4 text-sm studio-text-secondary', className)}><Loader2 className="h-4 w-4 animate-spin" /> Loading gates…</div>;
  }
  if (gates.length === 0) {
    return <div className={cn('p-4 text-xs studio-text-secondary', className)}>No interaction gates on this slide.</div>;
  }

  const heldIdx = heldGate ? gates.findIndex((g) => g.id === heldGate.id) : -1;
  const heldColor = heldIdx >= 0 ? GATE_COLORS[heldIdx % GATE_COLORS.length] : '#d97706';

  return (
    <div className={cn('space-y-3 p-4', className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-wider studio-text-secondary">Interaction Gates</h3>
        <span className="text-[10px] studio-text-secondary">{scaleDur > 0 ? `${scaleDur.toFixed(1)}s audio` : ''}</span>
      </div>

      {audioSrc && (
        <audio ref={audioRef} src={audioSrc} preload="metadata"
          onLoadedMetadata={(e) => { const d = (e.target as HTMLAudioElement).duration; if (Number.isFinite(d) && d > 0) setDuration(d); }}
          onPause={() => setIsPlaying(false)} onEnded={() => setIsPlaying(false)}
          onError={() => { if (audioSrc === narration.src) narration.onError(); }} />
      )}

      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <button type="button" onClick={togglePlay} disabled={!audioSrc}
            className="h-7 w-7 rounded-full bg-primary text-white flex items-center justify-center disabled:opacity-40">
            {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
          </button>
          <span className="text-[10px] tabular-nums studio-text-secondary">{fmt(currentTime)} / {fmt(scaleDur)}</span>
          <span className="ml-auto text-[10px] studio-text-secondary">play holds at each gate · click a breath ⎵ below to place</span>
        </div>

        <div ref={trackRef}
          onPointerDown={(e) => { if (e.target === e.currentTarget || (e.target as HTMLElement).dataset.seek) seek(xToTime(e.clientX)); }}
          className="relative rounded-lg bg-black/30 overflow-hidden cursor-pointer select-none" style={{ height: TRACK_H }}>
          {/* waveform */}
          <canvas ref={canvasRef} data-seek className="absolute inset-0 pointer-events-none" />
          {/* silences (snap targets) */}
          <div data-seek className="absolute inset-0">
            {silences.map((s, i) => (
              <div key={i} data-seek className="absolute top-0 h-full bg-emerald-400/15"
                style={{ left: `${pct(s.start)}%`, width: `${pct(s.end) - pct(s.start)}%` }} />
            ))}
          </div>
          {/* gate held regions + draggable handles */}
          {gates.map((g, gi) => {
            const color = GATE_COLORS[gi % GATE_COLORS.length];
            const isHeld = heldGate?.id === g.id;
            return (
              <div key={g.id}>
                <div className={cn('absolute top-0 h-full opacity-30', isHeld && 'animate-pulse opacity-60')}
                  style={{ left: `${pct(g.pauseAt)}%`, width: `${Math.max(0, pct(g.resumeAt) - pct(g.pauseAt))}%`, background: color }} />
                {(['pauseAt', 'resumeAt'] as const).map((edge) => (
                  <div key={edge}
                    onPointerDown={(e) => { e.stopPropagation(); (e.target as HTMLElement).setPointerCapture?.(e.pointerId); dragRef.current = { id: g.id, edge }; }}
                    title={`Drag ${g.id} ${edge === 'pauseAt' ? 'pause' : 'resume'}`}
                    className="absolute top-0 h-full w-5 -ml-2.5 cursor-ew-resize flex justify-center group touch-none"
                    style={{ left: `${pct(g[edge])}%` }}>
                    <div className="h-full w-[3px] rounded group-hover:w-[6px] transition-all" style={{ background: color }} />
                    <span className="absolute -top-1 left-1/2 -translate-x-1/2 text-[10px] font-bold" style={{ color }}>{edge === 'pauseAt' ? '⏸' : '▶'}</span>
                  </div>
                ))}
              </div>
            );
          })}
          {/* playhead */}
          <div className="absolute top-0 h-full w-[2px] bg-white pointer-events-none" style={{ left: `${pct(currentTime)}%` }} />
        </div>

        {/* the "common button" — appears when playback holds at a gate */}
        {heldGate && (
          <div className="flex items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: `${heldColor}66`, background: `${heldColor}1a` }}>
            <Hand className="h-3.5 w-3.5" style={{ color: heldColor }} />
            <span className="text-[11px] font-semibold" style={{ color: heldColor }}>
              Held at {heldGate.id}{heldGate.interaction?.mechanic ? ` · ${heldGate.interaction.mechanic}` : ''}
            </span>
            <button type="button" onClick={simulate}
              className="ml-auto h-7 px-3 rounded-md text-white text-[11px] font-bold flex items-center gap-1" style={{ background: heldColor }}>
              Simulate interaction <Play className="h-3 w-3" /> Resume
            </button>
          </div>
        )}
      </div>

      {gates.map((g, gi) => {
        const color = GATE_COLORS[gi % GATE_COLORS.length];
        return (
          <div key={g.id} className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold studio-text-primary flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-sm" style={{ background: color }} />{g.id}
              </span>
              <div className="flex items-center gap-1.5">
                {g.interaction?.mechanic && <span className="text-[10px] px-2 py-0.5 rounded-md bg-primary/10 text-primary">{g.interaction.mechanic}</span>}
                {audioSrc && (
                  <button type="button" onClick={() => previewGate(g)} title="Jump here and experience the pause"
                    className="text-[10px] px-2 py-0.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 studio-text-secondary flex items-center gap-1">
                    <Play className="h-2.5 w-2.5" /> Preview
                  </button>
                )}
              </div>
            </div>

            {/* Word-zoom: the words around this gate with the breaths between them
                clickable — click a breath (⎵) to drop the pause/resume exactly,
                no pixel-dragging. The current breath is highlighted. */}
            {wordTimings.length > 0 && (() => {
              const idx = Math.max(0, wordTimings.findIndex((w) => w.endTime >= g.pauseAt));
              const win = wordTimings.slice(Math.max(0, idx - 5), idx + 6);
              return (
                <div className="rounded-lg bg-black/20 px-2 py-2.5 flex flex-wrap items-center gap-y-2 leading-relaxed">
                  {win.map((w, i) => {
                    const next = win[i + 1];
                    const gap = next ? next.startTime - w.endTime : 0;
                    const isSilence = !!next && gap >= SILENCE_MIN;
                    const isCurrent = isSilence && Math.abs(w.endTime - g.pauseAt) < 0.2;
                    return (
                      <span key={i} className="flex items-center">
                        <span className="text-[12px] studio-text-primary">{w.word}</span>
                        {next && !isSilence && <span className="inline-block w-1.5" />}
                        {isSilence && (
                          <button type="button"
                            onClick={() => {
                              setGates((prev) => prev.map((x) => (x.id === g.id ? { ...x, pauseAt: Number(w.endTime.toFixed(3)), resumeAt: Number(next!.startTime.toFixed(3)) } : x)));
                              setDirty(true); setStatus(null); seek(w.endTime);
                            }}
                            title={`Pause in this breath — ${gap.toFixed(2)}s @ ${w.endTime.toFixed(2)}s`}
                            className={cn('mx-1.5 h-[18px] px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center border transition-all',
                              isCurrent
                                ? 'text-white border-transparent scale-110 shadow'
                                : 'border-dashed border-emerald-400/55 text-emerald-300/90 hover:bg-emerald-400/25 hover:scale-105')}
                            style={isCurrent ? { background: color } : undefined}>
                            {isCurrent ? '⏸▶' : '⎵'}
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              );
            })()}

            {/* fine control: nudge ±0.05s · exact value · drop at the playhead */}
            {(['pauseAt', 'resumeAt'] as const).map((edge) => (
              <div key={edge} className="flex items-center gap-1">
                <span className="w-14 text-[10px] studio-text-secondary">{edge === 'pauseAt' ? '⏸ Pause' : '▶ Resume'}</span>
                <button type="button" onClick={() => nudge(g.id, edge, -0.05)} title="−0.05s"
                  className="h-7 w-6 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-xs studio-text-secondary">−</button>
                <input type="number" step="0.05" min={0} value={g[edge]} onChange={(e) => setTiming(g.id, edge, parseFloat(e.target.value))}
                  className="w-14 rounded-md bg-black/20 border border-white/10 px-1 py-1 text-xs text-center studio-text-primary focus:outline-none focus:ring-1 focus:ring-primary" />
                <button type="button" onClick={() => nudge(g.id, edge, 0.05)} title="+0.05s"
                  className="h-7 w-6 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-xs studio-text-secondary">+</button>
                <span className="text-[10px] studio-text-secondary">s</span>
              </div>
            ))}

            {wordTimings.length > 0 && (
              <button type="button" onClick={() => snap(g.id)} title="Snap both edges to the nearest breath"
                className="w-full h-7 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-[10px] studio-text-secondary flex items-center justify-center gap-1">
                <AlignVerticalJustifyCenter className="h-3 w-3" /> Snap to nearest breath
              </button>
            )}
          </div>
        );
      })}

      {error && <p className="text-xs text-destructive">{error}</p>}
      {status && !error && <p className="text-xs studio-text-secondary">{status}</p>}

      <button type="button" onClick={save} disabled={!dirty || !!error || saving}
        className={cn('w-full h-9 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition',
          !dirty || !!error || saving ? 'bg-white/5 studio-text-secondary cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90')}>
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        {saving ? 'Saving…' : 'Save gate timings'}
      </button>
    </div>
  );
};

export default GateTimingEditor;
